import { Controller } from "@hotwired/stimulus"

/**
 * Mobile drawer for the full-page companion's chat-list rail.
 *
 * WHY THIS EXISTS. The docked rail is `hidden md:flex` — 300px of column on a
 * phone leaves nothing for the conversation. But the full-page route also
 * SUPPRESSES the companion panel (two `#ai-companion-messages` would break
 * card delivery), which removes the navbar sparkles toggle with it. Below `md`
 * that added up to a page with NO route to conversation history at all: the
 * column is hidden, and the panel that used to be the mobile fallback never
 * mounts. This controller gives the page header a history button that slides
 * the same docked rail in as an overlay drawer.
 *
 * Deliberately NOT the rail's own :overlay variant. That variant is built for
 * the 420px panel — collapsed 48px strip, scrim wired to the panel's collapse
 * cookie, auto-collapse on select — and reusing it here would re-couple the
 * page to `companion_rail_collapsed`, the exact cookie the docked variant must
 * never touch (a write here silently collapses the PANEL's rail next time it
 * opens). And not `ui--accordion` either: its open/close leaves inline
 * maxHeight/overflow styles on the content, which would clip the rail on
 * desktop after a mobile toggle followed by a rotation — `md:flex` can win
 * over a class, not over an inline style.
 *
 * Everything here toggles CLASSES only, and only classes that are inert at
 * `md+` (`hidden` loses to `md:flex`; the drawer positioning is all `max-md:`),
 * so desktop never sees any of it.
 *
 * Closes on: the toggle itself, Escape, scrim tap, and selecting a chat
 * (`ai--chat-list-rail:select-chat` bubbles to this element) — on a phone the
 * user picked a thread to READ it, and a drawer left covering it reads as
 * "tapping did nothing".
 */
export default class extends Controller {
  static targets = ["rail", "scrim", "toggleButton"]

  connect() {
    this.open = false

    this._handleEscape = (event) => {
      if (event.key !== "Escape") return
      if (!this.open) return
      this.close()
    }
    window.addEventListener("keydown", this._handleEscape)

    // The rail dispatches select-chat as a bubbling DOM event (that is its
    // whole contract with ai--companion), so it reaches this shared root too.
    this._handleSelectChat = () => this.close()
    this.element.addEventListener(
      "ai--chat-list-rail:select-chat",
      this._handleSelectChat,
    )
  }

  disconnect() {
    window.removeEventListener("keydown", this._handleEscape)
    this.element.removeEventListener(
      "ai--chat-list-rail:select-chat",
      this._handleSelectChat,
    )
  }

  toggle() {
    if (this.open) {
      this.close()
    } else {
      this.show()
    }
  }

  show() {
    this.open = true
    this.#sync()
  }

  close() {
    this.open = false
    this.#sync()
  }

  #sync() {
    if (this.hasRailTarget) {
      this.railTarget.classList.toggle("hidden", !this.open)
    }
    if (this.hasScrimTarget) {
      this.scrimTarget.classList.toggle("hidden", !this.open)
    }
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute("aria-expanded", String(this.open))
    }
  }
}
